import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { useEffect, useRef } from 'react'

/**
 * Hook to automatically refresh billing status when:
 * 1. Window regains focus (user returns from payment gateway)
 * 2. Component mounts (initial load)
 * 3. URL contains payment success parameters
 */
export function useBillingStatusRefresh(options?: {
	enabled?: boolean
	onFocus?: boolean
	organizationId?: string | null
}) {
	const { enabled = true, onFocus = true, organizationId } = options || {}
	const { fetchBillingStatus } = useSubscriptionStore()
	const hasRefreshedOnFocusRef = useRef(false)

	// Initial refresh on mount
	useEffect(() => {
		if (!enabled) {
			return
		}

		fetchBillingStatus(organizationId || undefined)
	}, [enabled, fetchBillingStatus, organizationId])

	// Refresh when window regains focus (user returns from payment gateway)
	useEffect(() => {
		if (!enabled || !onFocus) {
			return
		}

		const handleFocus = async () => {
			// Only refresh once per focus event to avoid multiple rapid refreshes
			if (hasRefreshedOnFocusRef.current) {
				hasRefreshedOnFocusRef.current = false
				return
			}

			hasRefreshedOnFocusRef.current = true

			// Small delay to ensure user has fully returned to the page
			setTimeout(async () => {
				await fetchBillingStatus(organizationId || undefined)
				hasRefreshedOnFocusRef.current = false
			}, 500)
		}

		window.addEventListener('focus', handleFocus)

		return () => {
			window.removeEventListener('focus', handleFocus)
		}
	}, [enabled, onFocus, fetchBillingStatus, organizationId])

	// Check URL parameters for payment success indicators
	useEffect(() => {
		if (!enabled) {
			return
		}

		const urlParams = new URLSearchParams(window.location.search)
		const paymentSuccess = urlParams.get('payment_success')
		const sessionId = urlParams.get('session_id')

		if (paymentSuccess === 'true' || sessionId) {
			// Refresh billing status if payment success parameter is present
			const refresh = async () => {
				await fetchBillingStatus(organizationId || undefined)

				// Clean up URL parameters
				urlParams.delete('payment_success')
				urlParams.delete('session_id')
				const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '')
				window.history.replaceState({}, '', newUrl)
			}

			refresh()
		}
	}, [enabled, fetchBillingStatus, organizationId])
}
